import { useEffect } from "react";
import { useEvents, useDonations, useActivities } from "@/lib/api/hooks";
import { useBlogs } from "@/lib/api/hooks/use-blogs";
import { useUserRole } from "@/lib/hooks/use-user-role";
import { PageHeader } from "@/components/layout/page-header";
import { Heart, Calendar, Activity, Users, FileText, Eye, FileEdit } from "lucide-react";
import { StatCard } from "./components/stat-card";
import { RecentDonations } from "./components/recent-donations";
import { UpcomingEvents } from "./components/upcoming-events";
import { ActivityChart } from "./components/activity-chart";
import { ActivityData, ActivityMetrics, DonationMetrics } from "@/lib/api/types";
import { create } from "zustand";

interface ActivityChartData {
  name: string;
  donations: number;
  events: number;
}

interface DashboardStore {
  activityData: ActivityChartData[];
  setActivityData: (data: ActivityChartData[]) => void;
}

const useDashboardStore = create<DashboardStore>((set) => ({
  activityData: [],
  setActivityData: (data) => set({ activityData: data }),
}));

function combineMetricsData(
  donationsMetrics: DonationMetrics | undefined,
  activitiesMetrics: ActivityMetrics | undefined
): ActivityChartData[] {
  if (!donationsMetrics?.monthlyStats || !activitiesMetrics?.monthlyStats) {
    return [];
  }

  const DEFAULT_MONTHS = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];
  
  return donationsMetrics.monthlyStats.map((donationStat, index) => ({
    name: DEFAULT_MONTHS[index] || `Month ${index + 1}`,
    donations: donationStat.donations,
    events: activitiesMetrics.monthlyStats[index]?.activities || 0
  }));
}

export default function DashboardPage() {
  const { data: userRole, isLoading: isRoleLoading } = useUserRole();
  const isAuthor = userRole?.isAuthor || false;
  const isAdmin = !isRoleLoading && !isAuthor;

  const { data: blogs = [], isLoading: blogsLoading } = useBlogs();

  // Admin hooks - only enabled for admins to prevent forbidden API calls
  const { events = [], isLoading: eventsLoading } = useEvents({ enabled: isAdmin });
  const {
    donations = [],
    isLoading: donationsLoading,
    metrics: { data: donationsMetrics, isLoading: donationsMetricsLoading }
  } = useDonations({ enabled: isAdmin });
  const {
    activities = [],
    isLoading: activitiesLoading,
    metrics: { data: activitiesMetrics, isLoading: activitiesMetricsLoading }
  } = useActivities({ enabled: isAdmin });
  const { activityData, setActivityData } = useDashboardStore();

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  useEffect(() => {
    if (!donationsMetricsLoading && !activitiesMetricsLoading && donationsMetrics && activitiesMetrics) {
      const combinedData = combineMetricsData(donationsMetrics, activitiesMetrics);
      setActivityData(combinedData);
    }
  }, [donationsMetrics, activitiesMetrics, donationsMetricsLoading, activitiesMetricsLoading, setActivityData]);

  // Calculate blog stats for authors
  const totalViews = blogs.reduce((acc, blog) => acc + (blog.viewCount || 0), 0);
  const publishedBlogs = blogs.filter(blog => blog.status === 'published').length;
  const draftBlogs = blogs.filter(blog => blog.status === 'draft').length;

  // For authors, skip loading and return early
  if (isRoleLoading || blogsLoading) {
    return <div className="text-foreground">loading....</div>;
  }

  if (isAuthor) {
    return (
      <div className="space-y-6 md:space-y-8">
        <PageHeader title="My Blog Stats" />

        <div className="grid gap-4 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Posts"
            value={blogs.length}
            icon={FileText}
            change={{ value: `${publishedBlogs} published`, positive: true }}
          />
          <StatCard
            title="Total Views"
            value={totalViews}
            icon={Eye}
            change={{ value: "", positive: true }}
          />
          <StatCard
            title="Published"
            value={publishedBlogs}
            icon={FileText}
            change={{ value: "", positive: true }}
          />
          <StatCard
            title="Drafts"
            value={draftBlogs}
            icon={FileEdit}
            change={{ value: "", positive: false }}
          />
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4 text-foreground">Recent Blog Posts</h2>
          {blogs.length === 0 ? (
            <p className="text-muted-foreground">No blog posts yet. Start writing your first post!</p>
          ) : (
            <div className="space-y-4">
              {blogs.slice(0, 5).map((blog) => (
                <div key={blog.id} className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
                  <div className="flex-1">
                    <h3 className="font-medium text-foreground">{blog.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {blog.status === 'published' ? 'Published' : 'Draft'} • {blog.viewCount || 0} views
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const totalRsvps = events.reduce((acc, event) => acc + (event.rsvpCount || 0), 0);
  const upcomingEvents = events
    .filter((event) => new Date(event.date) > new Date())
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(0, 5);

  const recentDonations = donations
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 5);

  if (eventsLoading || donationsLoading || activitiesLoading || donationsMetricsLoading || activitiesMetricsLoading) {
    return <div className="text-foreground">loading....</div>;
  }

  // Admin Dashboard
  return (
    <div className="space-y-6 md:space-y-8">
      <PageHeader title="Dashboard Overview" />

      <div className="grid gap-4 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Donations"
          value={donations.length}
          icon={Heart}
          change={{ value: "", positive: true }}
        />
        <StatCard
          title="Upcoming Events"
          value={events.length}
          icon={Calendar}
          change={{ value: "", positive: true }}
        />
        <StatCard
          title="Total Activities"
          value={activities.length}
          icon={Activity}
          change={{ value: "", positive: true }}
        />
        <StatCard
          title="Event Registrations"
          value={totalRsvps}
          icon={Users}
          change={{ value: "", positive: true }}
        />
      </div>

      <div className="grid gap-4 md:gap-6 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <ActivityChart data={activityData} />
        </div>
        <div className="flex flex-col gap-4 md:gap-6">
          <RecentDonations donations={recentDonations} />
          <UpcomingEvents events={upcomingEvents} />
        </div>
      </div>
    </div>
  );
}